import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ClipboardCheck,
  Wallet,
  ScanLine,
  MonitorCheck,
  LogOut,
  Languages,
  GraduationCap,
  CalendarDays,
  FileSpreadsheet,
  ReceiptText,
  UserCheck,
  Menu,
  QrCode,
} from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const linksByRole = {
  admin: [
    { to: "/admin", key: "dashboard", icon: LayoutDashboard },
    { to: "/admin/students", key: "students", icon: GraduationCap },
    { to: "/admin/users", key: "users", icon: Users },
    { to: "/admin/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/admin/timetable", key: "timetable", icon: CalendarDays },
    { to: "/admin/enrollment", key: "enrollment", icon: UserCheck },
    { to: "/admin/fees", key: "fees", icon: ReceiptText },
    { to: "/admin/payments", key: "payments", icon: Wallet },
    { to: "/admin/reports", key: "reports", icon: FileSpreadsheet },
    { to: "/admin/qr", key: "wallQR", icon: QrCode },
    { to: "/kiosk", key: "kiosk", icon: MonitorCheck },
  ],
  staff: [
    { to: "/staff", key: "dashboard", icon: LayoutDashboard },
    { to: "/staff/students", key: "students", icon: GraduationCap },
    { to: "/staff/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/kiosk", key: "kiosk", icon: MonitorCheck },
  ],
  teacher: [
    { to: "/teacher", key: "dashboard", icon: LayoutDashboard },
    { to: "/teacher/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/teacher/reports", key: "classReport", icon: FileSpreadsheet },
  ],
  parent: [
    { to: "/parent", key: "dashboard", icon: LayoutDashboard },
    { to: "/parent/children", key: "myChildren", icon: GraduationCap },
    { to: "/parent/attendance", key: "attendance", icon: ClipboardCheck },
    { to: "/parent/fees", key: "fees", icon: ReceiptText },
    { to: "/parent/checkin", key: "scanQR", icon: ScanLine },
    { to: "/parent/timetable", key: "timetable", icon: CalendarDays },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return children;
  const links = linksByRole[user.role] || [];

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen grain-bg">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 border-b border-border/70 bg-background/80 backdrop-blur"
        data-testid="topbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-1">
            {/* Mobile sidebar (hamburger) */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-xl md:hidden"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetHeader className="border-b border-border/70 p-4 text-left">
                  <SheetTitle className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground">
                      <GraduationCap className="h-5 w-5" />
                    </div>
                    <span className="font-display text-lg font-bold">{t("appName")}</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 p-3" data-testid="mobile-sidebar-nav">
                  {links.map((l) => {
                    const Icon = l.icon;
                    const active =
                      loc.pathname === l.to ||
                      (l.to !== `/${user.role}` && loc.pathname.startsWith(l.to));
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`mnav-${l.key}`}
                        className={cn(
                          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                          active
                            ? "bg-primary text-primary-foreground"
                            : "text-foreground/80 hover:bg-muted",
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {t(l.key)}
                      </Link>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
            <Link to={`/${user.role}`} className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-lg font-bold text-foreground">
                  {t("appName")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t(`${user.role}_dashboard_title`)}
                </div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === "en" ? "ms" : "en")}
              data-testid="lang-toggle"
              className="gap-2 rounded-full"
            >
              <Languages className="h-4 w-4" />
              <span className="hidden sm:inline">{t("switchLanguage")}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={doLogout}
              data-testid="logout-btn"
              className="gap-2 rounded-full"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Body layout — sidebar on md+, bottom nav on mobile */}
      <div className="mx-auto flex max-w-7xl gap-6 px-4 pb-24 pt-6 sm:px-6 md:pb-6">
        <aside className="hidden w-56 shrink-0 md:block">
          <nav className="sticky top-24 flex flex-col gap-1">
            {links.map((l) => {
              const Icon = l.icon;
              const active =
                loc.pathname === l.to ||
                (l.to !== `/${user.role}` && loc.pathname.startsWith(l.to));
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.key}`}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground/80 hover:bg-muted",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t(l.key)}
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">{children}</main>
      </div>

      {/* Bottom nav mobile */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border/70 bg-background/95 backdrop-blur md:hidden"
        data-testid="bottom-nav"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-5 gap-1 px-2 py-2">
          {links.slice(0, 5).map((l) => {
            const Icon = l.icon;
            const active =
              loc.pathname === l.to ||
              (l.to !== `/${user.role}` && loc.pathname.startsWith(l.to));
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`bnav-${l.key}`}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-[11px]",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground",
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="leading-none">{t(l.key)}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
